import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@marketingbee/contracts';

/**
 * MarketingBee plans: the single source of truth for what each plan costs, includes and unlocks. The
 * API enforces it, the web app renders it, Stripe prices must match it, and Hive pushes the same
 * values through `/hive/v1/entitlements`.
 *
 * Vendored slice of WRKRBZ `_shared/catalog.json` (version 2026-10-15, bee `marketingbee`). Hive owns
 * the catalog: change prices there first, then copy the slice here. Never change prices locally.
 */
export const CATALOG_VERSION = '2026-10-15';
export const BEE_ID = 'marketingbee' as const;
export const TRIAL_DAYS = 7;

export const PAID_PLANS = ['mkt_starter', 'mkt_growth'] as const;
export type PaidPlanId = (typeof PAID_PLANS)[number];

/** Every feature id any MarketingBee plan can carry (catalog `features`). */
export const PLAN_FEATURES = ['brand_brain', 'content_planner', 'drafts', 'approvals', 'social_publishing', 'link_tracking', 'email_marketing', 'blog', 'analytics', 'multi_brand', 'api'] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

export const PLAN_FEATURE_LABELS: Record<PlanFeature, string> = {
  brand_brain: 'Brand profile, audiences and pillars',
  content_planner: 'Weekly AI content plan',
  drafts: 'AI drafts',
  approvals: 'Approval before anything posts',
  social_publishing: 'Social publishing',
  link_tracking: 'Tracked links',
  email_marketing: 'Email marketing and sequences',
  blog: 'Blog post drafts',
  analytics: 'Results by channel and campaign',
  multi_brand: 'More than one brand',
  api: 'API keys and webhooks',
};

/** Limit keys used by the catalog. Null = unlimited. `emailContacts` 0 = no email marketing. */
export type PlanLimits = { brands: number | null; channels: number | null; postsPerMonth: number | null; emailContacts: number | null };
export const LIMIT_KEYS = ['brands', 'channels', 'postsPerMonth', 'emailContacts'] as const satisfies readonly (keyof PlanLimits)[];

export type PlanDefinition = {
  id: PaidPlanId;
  name: string;
  tagline: string;
  /** CAD cents per month. */
  priceCents: number;
  currency: 'cad';
  interval: 'month';
  lookupKey: string;
  limits: PlanLimits;
  features: readonly PlanFeature[];
  highlights: readonly string[];
  badge?: string;
};

export const PLANS: Record<PaidPlanId, PlanDefinition> = {
  mkt_starter: {
    id: 'mkt_starter',
    name: 'MarketingBee Starter',
    tagline: 'An AI marketing team: plans, drafts and publishes your posts after you approve them.',
    priceCents: 14900,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'marketingbee_starter_monthly',
    limits: { brands: 1, channels: 3, postsPerMonth: 30, emailContacts: 0 },
    features: ['brand_brain', 'content_planner', 'drafts', 'approvals', 'social_publishing', 'link_tracking'],
    highlights: ['Brand profile, audiences and content pillars', 'A weekly plan drafted by AI', '30 posts a month on 3 channels', 'Nothing goes out until you approve it', 'Tracked links on every post'],
  },
  mkt_growth: {
    id: 'mkt_growth',
    name: 'MarketingBee Growth',
    tagline: 'Everything in Starter plus email, blog, results by channel and up to 3 brands.',
    priceCents: 39900,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'marketingbee_growth_monthly',
    limits: { brands: 3, channels: null, postsPerMonth: 150, emailContacts: 10000 },
    features: ['brand_brain', 'content_planner', 'drafts', 'approvals', 'social_publishing', 'link_tracking', 'email_marketing', 'blog', 'analytics', 'multi_brand', 'api'],
    highlights: ['Everything in Starter', '150 posts a month, every channel', 'Email to up to 10,000 opted-in contacts', 'Blog post drafts', 'Results by channel and campaign', 'Up to 3 brands'],
    badge: 'Most complete',
  },
};

/** Metered usage reported to Hive (catalog `metered`): none for MarketingBee. */
export const METERED = [] as const;

/** Quota metrics counted per calendar month (UTC): published social posts. */
export const QUOTA_METRICS = ['posts'] as const;
export type QuotaMetric = (typeof QUOTA_METRICS)[number];

export function isPaidPlan(plan: string | null | undefined): plan is PaidPlanId {
  return plan === 'mkt_starter' || plan === 'mkt_growth';
}

export function planForLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => PLANS[p].lookupKey === lookupKey) ?? null;
}

/**
 * Standalone (non-Hive) Stripe prices can live in the same Stripe account as Hive's, which owns
 * the catalog lookup keys, so standalone prices use `standalone_<catalog key>`.
 */
export const standaloneLookupKey = (catalogLookupKey: string) => `standalone_${catalogLookupKey}`;

export function planForStandaloneLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => standaloneLookupKey(PLANS[p].lookupKey) === lookupKey) ?? null;
}

const CATALOG_PLAN_CENTS = Object.fromEntries(PAID_PLANS.map((id) => [id, PLANS[id].priceCents])) as Record<PaidPlanId, number>;

/** The standalone Stripe lookup keys whose live amounts MarketingBee shows and charges. */
export function standaloneLookupKeys(): string[] {
  return PAID_PLANS.map((id) => standaloneLookupKey(PLANS[id].lookupKey));
}

/**
 * Prices are edited on Hive's Pricing page, which creates new standalone Stripe prices. The API
 * reads their amounts (by lookup key) and applies them here, so PLANS match what Checkout charges.
 * A key missing from `amounts` uses the catalog price.
 */
export function applyStandalonePrices(amounts: Readonly<Record<string, number>>): void {
  for (const id of PAID_PLANS) (PLANS[id] as { priceCents: number }).priceCents = amounts[standaloneLookupKey(PLANS[id].lookupKey)] ?? CATALOG_PLAN_CENTS[id];
}

export type BillingState = 'trialing' | 'active' | 'past_due' | 'inactive' | 'suspended';

/** The workspace columns entitlements are computed from. */
export type BillingFields = {
  plan: string;
  billingStatus?: string | null;
  trialEndsAt: Date | null;
  subscriptionStatus?: string | null;
  suspendedAt?: Date | null;
  deleteRequestedAt?: Date | null;
  hiveManaged?: boolean | null;
  /** Last Hive push (Hive-managed only). */
  entitlementFeatures?: string[] | null;
  entitlementLimits?: Record<string, number | null> | null;
};

export type Entitlements = {
  plan: string;
  planName: string;
  state: BillingState;
  /** False when the trial/subscription ended or the workspace is suspended: read-only. */
  active: boolean;
  features: PlanFeature[];
  limits: PlanLimits;
  trialEndsAt: Date | null;
  trialDaysLeft: number | null;
  hiveManaged: boolean;
};

const LIVE_STRIPE = new Set(['active', 'trialing', 'past_due']);

function knownFeatures(list: readonly string[]): PlanFeature[] {
  return list.filter((f): f is PlanFeature => (PLAN_FEATURES as readonly string[]).includes(f));
}

function limitValue(raw: Record<string, number | null> | null | undefined, key: keyof PlanLimits, fallback: number | null): number | null {
  if (!raw || !(key in raw)) return fallback;
  const v = raw[key];
  return v === null || v === undefined ? null : Number(v);
}

/**
 * What a workspace may do right now.
 *  - Hive-managed: plan, status, features and limits come from the last entitlements push.
 *  - Standalone: the plan definition above, with the trial or the Stripe subscription status.
 *  - `inactive` / `suspended` (or a passed trial) make the workspace read-only.
 */
export function entitlements(w: BillingFields, now: Date = new Date()): Entitlements {
  const def = isPaidPlan(w.plan) ? PLANS[w.plan] : PLANS.mkt_starter;
  const hiveManaged = Boolean(w.hiveManaged);
  const trialEndsAt = w.trialEndsAt ?? null;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / 86_400_000)) : null;

  let state: BillingState;
  if (w.suspendedAt || w.billingStatus === 'suspended') state = 'suspended';
  else if (w.deleteRequestedAt) state = 'inactive';
  else if (hiveManaged) state = (w.billingStatus as BillingState | null) ?? 'active';
  else if (w.subscriptionStatus) state = !LIVE_STRIPE.has(w.subscriptionStatus) ? 'inactive' : w.subscriptionStatus === 'past_due' ? 'past_due' : 'active';
  else state = (w.billingStatus as BillingState | null) ?? 'trialing';

  if (state === 'trialing' && trialEndsAt && trialEndsAt.getTime() <= now.getTime()) state = 'inactive';
  if (!['trialing', 'active', 'past_due', 'inactive', 'suspended'].includes(state)) state = 'inactive';
  const active = state === 'trialing' || state === 'active' || state === 'past_due';

  const features = hiveManaged && w.entitlementFeatures ? knownFeatures(w.entitlementFeatures) : [...def.features];
  const limits: PlanLimits = hiveManaged
    ? (Object.fromEntries(LIMIT_KEYS.map((k) => [k, limitValue(w.entitlementLimits, k, def.limits[k])])) as PlanLimits)
    : { ...def.limits };

  return {
    plan: def.id,
    planName: def.name,
    state,
    active,
    features: active ? features : [],
    limits,
    trialEndsAt: state === 'trialing' || state === 'inactive' ? trialEndsAt : null,
    trialDaysLeft: state === 'trialing' ? trialDaysLeft : null,
    hiveManaged,
  };
}

export function hasFeature(e: Pick<Entitlements, 'features'>, feature: PlanFeature): boolean {
  return e.features.includes(feature);
}

/**
 * tRPC procedure paths that need a plan feature, matched by prefix (first match wins, so list the
 * more specific prefixes first). Procedures added to these routers later are gated automatically.
 */
export const FEATURE_PATHS: readonly [prefix: string, feature: PlanFeature][] = [
  ['integrations.apiKeys.', 'api'],
  ['integrations.webhooks.', 'api'],
  ['sequences.', 'email_marketing'],
  ['email.', 'email_marketing'],
  ['analytics.', 'analytics'],
  ['links.', 'link_tracking'],
  ['channels.', 'social_publishing'],
  ['content.plan', 'content_planner'],
  ['content.redraft', 'drafts'],
  ['content.', 'drafts'],
  ['brand.', 'brand_brain'],
];

export function featureForPath(path: string): PlanFeature | null {
  return FEATURE_PATHS.find(([prefix]) => path.startsWith(prefix))?.[1] ?? null;
}

/** Mutations still allowed when inactive: billing, members, settings, profile, data export. */
export function allowedWhenInactive(path: string): boolean {
  return path.startsWith('workspace.') || path === 'crm.importExport.export';
}

/** `YYYY-MM` (UTC) period a quota counter belongs to. */
export function quotaPeriod(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7);
}

/** First instant of the next quota period (UTC). */
export function nextPeriodStart(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

export type QuotaCheck = { allowed: boolean; limit: number | null; used: number; remaining: number | null; reason: string | null };

/** Whether `requested` more units fit in the monthly allowance. */
export function checkQuota(metric: QuotaMetric, limits: PlanLimits, used: number, requested = 1): QuotaCheck {
  const limit = limits.postsPerMonth;
  if (limit === null) return { allowed: true, limit, used, remaining: null, reason: null };
  const remaining = Math.max(0, limit - used);
  if (requested <= remaining) return { allowed: true, limit, used, remaining, reason: null };
  return {
    allowed: false,
    limit,
    used,
    remaining,
    reason: `Your plan includes ${limit} ${metric} a month and this workspace has published ${used}. The allowance resets on the 1st; upgrade for more.`,
  };
}

/** Whether one more of `what` (brands, connected channels) fits the plan. */
export function withinCount(limit: number | null, current: number): boolean {
  return limit === null || current < limit;
}

export function formatPrice(def: Pick<PlanDefinition, 'priceCents'> & { currency?: string }): string {
  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency: (def.currency ?? DEFAULT_CURRENCY).toUpperCase(),
    minimumFractionDigits: def.priceCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(def.priceCents / 100);
}

export function upgradeMessage(feature: PlanFeature, hiveManaged: boolean): string {
  return `${PLAN_FEATURE_LABELS[feature]} is part of MarketingBee Growth. ${hiveManaged ? 'Upgrade in Hive.' : 'Upgrade in Settings → Billing.'}`;
}

export const INACTIVE_MESSAGE =
  'This workspace is read-only: its trial or subscription has ended, or it was suspended. Owners can choose a plan in Settings → Billing; your data is kept and can be exported.';
